/**
 * AttachmentSection Component
 * Displays and manages file attachments directly on assignments
 */

import { useState, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Paperclip,
  ChevronDown,
  ChevronUp,
  Download,
  FileText,
  Eye,
  Trash2,
  Upload,
  Loader2,
  X,
  Clock
} from 'lucide-react'
import { format } from 'date-fns'
import toast from 'react-hot-toast'
import {
  getAttachments,
  uploadAttachment,
  deleteAttachment
} from '../../services/assignmentAttachments'
import type { AssignmentAttachment } from '../../types/assignmentAttachment'

interface AttachmentSectionProps {
  assignmentId: string
  canUpload: boolean
  defaultCollapsed?: boolean
}

const isImageFile = (fileType: string) => fileType.startsWith('image/')

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function AttachmentSection({
  assignmentId,
  canUpload,
  defaultCollapsed = true
}: AttachmentSectionProps) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed)
  const [isUploading, setIsUploading] = useState(false)
  const [previewImage, setPreviewImage] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  const queryKey = ['assignment-attachments', assignmentId]

  const {
    data: attachments = [],
    isLoading
  } = useQuery({
    queryKey,
    queryFn: () => getAttachments(assignmentId),
    enabled: !!assignmentId && !isCollapsed
  })

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    setIsUploading(true)

    try {
      const uploadPromises = Array.from(files).map(file => uploadAttachment(assignmentId, file))
      const results = await Promise.all(uploadPromises)

      const succeeded = results.filter(r => r.success).length
      const failed = results.filter(r => !r.success)

      if (succeeded > 0) {
        toast.success(`${succeeded} ${succeeded === 1 ? 'bilaga uppladdad' : 'bilagor uppladdade'}`)
        queryClient.invalidateQueries({ queryKey })
      }

      if (failed.length > 0) {
        const errorMsg = failed[0].error || 'Kunde inte ladda upp fil'
        toast.error(failed.length === 1 ? errorMsg : `${failed.length} filer kunde inte laddas upp: ${errorMsg}`)
      }
    } catch (error) {
      console.error('Error uploading attachments:', error)
      toast.error('Något gick fel vid uppladdning')
    } finally {
      setIsUploading(false)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleDelete = async (attachmentId: string) => {
    if (!confirm('Är du säker på att du vill ta bort denna bilaga?')) return

    const result = await deleteAttachment(attachmentId)
    if (result.success) {
      toast.success('Bilaga borttagen')
      queryClient.invalidateQueries({ queryKey })
    } else {
      toast.error(result.error || 'Kunde inte ta bort bilaga')
    }
  }

  const renderAttachment = (attachment: AssignmentAttachment) => {
    const isImage = isImageFile(attachment.file_type)

    return (
      <div
        key={attachment.id}
        className="flex items-center gap-3 p-3 bg-nature-50 dark:bg-nature-700 rounded-lg group"
      >
        {isImage && attachment.url ? (
          <button
            onClick={() => setPreviewImage(attachment.url || null)}
            className="relative w-16 h-16 rounded overflow-hidden hover:opacity-80 transition-opacity flex-shrink-0"
          >
            <img
              src={attachment.url}
              alt={attachment.file_name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
              <Eye className="h-4 w-4 text-white" />
            </div>
          </button>
        ) : (
          <div className="w-10 h-10 rounded bg-nature-200 dark:bg-nature-600 flex items-center justify-center flex-shrink-0">
            <FileText className="h-5 w-5 text-nature-500 dark:text-nature-400" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-nature-900 dark:text-nature-100 truncate">
            {attachment.file_name}
          </p>
          <div className="flex items-center gap-2 text-xs text-nature-500">
            <span>{formatFileSize(attachment.file_size)}</span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {format(new Date(attachment.created_at), 'yyyy-MM-dd HH:mm')}
            </span>
            <span>av {attachment.uploaded_by_name}</span>
          </div>
        </div>

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {attachment.url && (
            <a
              href={attachment.url}
              download={attachment.file_name}
              className="p-1.5 text-nature-500 hover:text-nature-700 dark:hover:text-nature-300"
              title="Ladda ner"
            >
              <Download className="h-4 w-4" />
            </a>
          )}
          {canUpload && (
            <button
              onClick={() => handleDelete(attachment.id)}
              className="p-1.5 text-red-500 hover:text-red-700"
              title="Ta bort"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700">
        {/* Header */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-nature-50 dark:hover:bg-nature-700 transition-colors rounded-t-lg"
        >
          <div className="flex items-center space-x-2">
            <Paperclip className="h-5 w-5 text-nature-500" />
            <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
              Bilagor
            </h3>
            {!isCollapsed && (
              <span className="text-sm text-nature-500">
                ({attachments.length} {attachments.length === 1 ? 'bilaga' : 'bilagor'})
              </span>
            )}
          </div>
          {isCollapsed ? (
            <ChevronDown className="h-4 w-4 text-nature-400" />
          ) : (
            <ChevronUp className="h-4 w-4 text-nature-400" />
          )}
        </button>

        {/* Content */}
        {!isCollapsed && (
          <div className="px-6 pb-6">
            {/* Upload button */}
            {canUpload && (
              <div className="mb-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx"
                  onChange={handleUpload}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-nature-700 dark:text-nature-300 bg-nature-100 dark:bg-nature-700 hover:bg-nature-200 dark:hover:bg-nature-600 rounded-lg transition-colors disabled:opacity-50"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Laddar upp...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      Bifoga fil
                    </>
                  )}
                </button>
                <p className="mt-1 text-xs text-nature-500">
                  Max 10 MB. PDF, bilder, Word, Excel.
                </p>
              </div>
            )}

            {/* Attachments list */}
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-nature-500" />
              </div>
            ) : attachments.length === 0 ? (
              <div className="text-center py-6 text-nature-500">
                Inga bilagor ännu
              </div>
            ) : (
              <div className="space-y-2">
                {attachments.map(renderAttachment)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setPreviewImage(null)}
        >
          <button
            onClick={() => setPreviewImage(null)}
            className="absolute top-4 right-4 p-2 text-white hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={previewImage}
            alt="Förhandsgranskning"
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  )
}
